// ACTA_NOTIFICATION_PERSONAL_ONLY_V1
// ACTA_NOTIFICATION_MOBILE_FIXED_SAVE_V3
// ACTA_NOTIFICATION_MOBILE_COMPACT_V2
// ACTA_NOTIFICATION_MOBILE_CARDS_V1
// ACTA_NOTIFICATION_OPT_IN_PER_EVENT_V2
// acta_patch_notification_personal_admin_gmao_v1
import React, { useEffect, useMemo, useState } from "react";
import {
  BellSimple,
  CaretDown,
  CheckCircle,
  DeviceMobile,
  EnvelopeSimple,
  LockKey,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { notificationSettings } from "../api";
import { useAuth } from "../context/AuthContext";
import {
  getExistingPushSubscription,
  syncExistingPushSubscription,
} from "../lib/pushNotifications";


function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}


// ACTA_SAFARI_PUSH_DIAGNOSTIC_V1
function actaPushEnvironment() {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined"
  ) {
    return {
      isIos: false,
      isMobileDevice: false,
      isStandalone: false,
      isSecureContext: false,
    };
  }

  const userAgent =
    navigator.userAgent || "";

  const isIpadDesktopMode =
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1;

  const isIos =
    /iPhone|iPad|iPod/i.test(
      userAgent
    ) ||
    isIpadDesktopMode;

  const isMobileDevice =
    isIos ||
    /Android/i.test(userAgent);

  const isStandalone =
    window.matchMedia?.(
      "(display-mode: standalone)"
    )?.matches === true ||
    navigator.standalone === true;

  const isSecureContext =
    window.isSecureContext === true ||
    [
      "localhost",
      "127.0.0.1",
    ].includes(
      window.location.hostname
    );

  return {
    isIos,
    isMobileDevice,
    isStandalone,
    isSecureContext,
  };
}


function actaPushStatusLabel(status) {
  if (
    status.isIos &&
    !status.isStandalone
  ) {
    return "Installation requise sur iPhone";
  }

  if (!status.isSecureContext) {
    return "Connexion sécurisée requise";
  }

  if (!status.supported) {
    return "Non supporté dans ce mode";
  }

  if (
    status.reason ===
    "server_sync_error"
  ) {
    return "Synchronisation à réessayer";
  }

  if (status.subscribed) {
    return "Mobile enregistré";
  }

  if (
    status.permission === "denied"
  ) {
    return "Autorisation bloquée";
  }

  if (
    status.reason ===
    "service_worker_missing"
  ) {
    return "Activation requise";
  }

  return "Appareil non enregistré";
}


function actaPushHelpText(status) {
  if (
    status.isIos &&
    !status.isStandalone
  ) {
    return (
      "Sur iPhone ou iPad : ouvrez cette page dans Safari, " +
      "touchez Partager, puis Ajouter à l’écran d’accueil. " +
      "Ouvrez ensuite AlmaCRM depuis son icône avant d’activer le push."
    );
  }

  if (!status.isSecureContext) {
    return (
      "Les notifications push nécessitent une connexion HTTPS sécurisée."
    );
  }

  if (!status.supported) {
    return (
      "Ce navigateur ou ce mode d’ouverture ne fournit pas les fonctions Web Push nécessaires."
    );
  }

  if (
    status.permission === "denied"
  ) {
    return (
      "L’autorisation est bloquée. Réactivez AlmaCRM dans les réglages de notifications de l’appareil."
    );
  }

  if (
    status.reason ===
    "server_sync_error"
  ) {
    return (
      "L’autorisation du téléphone est conservée, mais Acta n’a pas pu la synchroniser. Réessayez sans modifier les réglages du téléphone."
    );
  }

  if (status.subscribed) {
    return (
      "Ce mobile est enregistré. Aucune nouvelle validation n’est nécessaire."
    );
  }

  if (
    status.reason ===
    "status_error"
  ) {
    return (
      status.error ||
      "Le statut du service de notifications n’a pas pu être vérifié."
    );
  }

  return (
    "Activez le push sur cet appareil avant d’envoyer une notification de test."
  );
}


const ROLE_LABELS = {
  admin: "Administrateur",
  manager: "Responsable commercial",
  user: "Commercial",
  maintenance_manager: "Responsable maintenance",
  technician: "Technicien",
};


const CATEGORY_META = {
  mail: {
    label: "Messagerie",
    description: "Nouveaux messages reçus dans la messagerie Acta.",
  },
  documents: {
    label: "Documents et partages",
    description: "Ouvertures, consultations, messages, réouvertures et expirations.",
  },
  meetings: {
    label: "Rendez-vous",
    description: "Demandes, créneaux proposés, acceptations et refus.",
  },
  gmao: {
    label: "GMAO",
    description: "Machines, maintenances et interventions techniques.",
  },
  sav: {
    label: "SAV",
    description: "Pannes, demandes clients et tickets de service après-vente.",
  },
  support: {
    label: "Support",
    description: "Réponses du support et signalements à vérifier.",
  },
};


const CATEGORY_ORDER = ["mail", "documents", "meetings", "gmao", "sav", "support"];


function errorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}


function ChannelSwitch({ checked, disabled, label, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={[
        "relative inline-flex h-6 w-11 items-center rounded-full border transition",
        checked
          ? "border-[var(--do-accent)] bg-[var(--do-accent)]"
          : "border-[var(--do-border)] bg-[var(--do-bg-2)]",
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
      ].join(" ")}
      data-testid={testId}
    >
      <span
        className={[
          "h-4 w-4 rounded-full bg-[var(--do-bg-solid)] shadow-sm transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        ].join(" ")}
      />
    </button>
  );
}


export default function NotificationSettings({ embedded = false }) {
  const { user } = useAuth();

  const pushEnvironment =
    actaPushEnvironment();

  const isMobileDevice =
    pushEnvironment.isMobileDevice;

  const role = user?.role || "user";
  const isSaasAdmin = role === "saas_admin";

  const [tenantEvents, setTenantEvents] = useState({});
  const [roles, setRoles] = useState({});
  const [platformEvents, setPlatformEvents] = useState({});
  const [platformRules, setPlatformRules] = useState({});
  const [prefs, setPrefs] = useState({ schemaVersion: 2, events: {} });
  const [openCategory, setOpenCategory] = useState("documents");
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const [pushStatus, setPushStatus] = useState({
    supported: false,
    permission: "default",
    subscribed: false,
    isIos: pushEnvironment.isIos,
    isStandalone:
      pushEnvironment.isStandalone,
    isSecureContext:
      pushEnvironment.isSecureContext,
    serviceWorkerReady: false,
    reason: "checking",
    error: "",
  });


  const readPushStatus = async () => {
    const environment =
      actaPushEnvironment();

    const apiSupported =
      typeof navigator !==
        "undefined" &&
      "serviceWorker" in navigator &&
      typeof window !==
        "undefined" &&
      "PushManager" in window &&
      "Notification" in window;

    const permission =
      typeof Notification !==
        "undefined"
        ? Notification.permission
        : "unsupported";

    if (
      environment.isIos &&
      !environment.isStandalone
    ) {
      const status = {
        supported: apiSupported,
        permission,
        subscribed: false,
        isIos: true,
        isStandalone: false,
        isSecureContext:
          environment.isSecureContext,
        serviceWorkerReady: false,
        reason:
          "ios_install_required",
        error: "",
      };

      setPushStatus(status);
      return status;
    }

    if (
      !environment.isSecureContext
    ) {
      const status = {
        supported: false,
        permission,
        subscribed: false,
        ...environment,
        serviceWorkerReady: false,
        reason: "insecure_context",
        error: "",
      };

      setPushStatus(status);
      return status;
    }

    if (!apiSupported) {
      const status = {
        supported: false,
        permission: "unsupported",
        subscribed: false,
        ...environment,
        serviceWorkerReady: false,
        reason: "unsupported",
        error: "",
      };

      setPushStatus(status);
      return status;
    }

    try {
      const registration =
        await navigator
          .serviceWorker
          .getRegistration("/");

      const subscription =
        await getExistingPushSubscription();

      const status = {
        supported: true,
        permission,
        subscribed:
          Boolean(subscription),
        ...environment,
        serviceWorkerReady:
          Boolean(
            registration?.active
          ),
        reason:
          subscription
            ? "ready"
            : permission === "denied"
              ? "permission_denied"
              : registration
                ? "not_subscribed"
                : "service_worker_missing",
        error: "",
      };

      setPushStatus(status);
      return status;
    } catch (error) {
      const status = {
        supported: true,
        permission,
        subscribed: false,
        ...environment,
        serviceWorkerReady: false,
        reason: "status_error",
        error:
          error?.message ||
          "Statut push indisponible",
      };

      setPushStatus(status);
      return status;
    }
  };


  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const prefData = await notificationSettings.me();
        setPrefs({
          schemaVersion: 2,
          events: prefData.events || {},
        });

        if (isSaasAdmin) {
          const platformData = await notificationSettings.platform();
          setPlatformEvents(platformData.events || {});
          setPlatformRules(platformData.rules || {});
        } else {
          const tenantData = await notificationSettings.tenant();
          setTenantEvents(tenantData.events || {});
          setRoles(tenantData.roles || {});
        }

        const currentPushStatus =
          await readPushStatus();

        if (
          currentPushStatus.subscribed
        ) {
          try {
            await syncExistingPushSubscription();
          } catch (error) {
            setPushStatus(
              (current) => ({
                ...current,
                subscribed: false,
                reason:
                  "server_sync_error",
                error:
                  error?.message ||
                  "Synchronisation push impossible",
              })
            );
          }
        }
      } catch (error) {
        toast.error(errorMessage(error, "Impossible de charger les notifications."));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isSaasAdmin]);


  const eligibleEvents = useMemo(
    () =>
      Object.entries(tenantEvents).filter(([eventKey]) => {
        const permission = roles?.[role]?.[eventKey] || {};
        return Boolean(permission.email || permission.push);
      }),
    [role, roles, tenantEvents]
  );


  const groupedEvents = useMemo(() => {
    const groups = {};

    eligibleEvents.forEach(([eventKey, event]) => {
      const declaredCategory =
        event.category ||
        (eventKey.startsWith("gmao.")
          ? "gmao"
          : eventKey.includes("meeting") || eventKey.includes("appointment")
            ? "meetings"
            : "documents");

      const category =
        declaredCategory === "gmao" &&
        /(sav|ticket)/i.test(eventKey)
          ? "sav"
          : declaredCategory;

      groups[category] = groups[category] || [];
      groups[category].push({ eventKey, event });
    });

    return CATEGORY_ORDER
      .filter((category) => groups[category]?.length)
      .map((category) => ({ category, items: groups[category] }));
  }, [eligibleEvents]);


  const togglePreference = (eventKey, channel) => {
    if (!roles?.[role]?.[eventKey]?.[channel]) return;

    setPrefs((current) => ({
      ...current,
      events: {
        ...(current.events || {}),
        [eventKey]: {
          email: Boolean(current.events?.[eventKey]?.email),
          push: Boolean(current.events?.[eventKey]?.push),
          [channel]: !Boolean(current.events?.[eventKey]?.[channel]),
        },
      },
    }));
  };


  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const result = await notificationSettings.updateMe({
        events: prefs.events || {},
      });
      setPrefs({
        schemaVersion: 2,
        events: result.preferences?.events || prefs.events || {},
      });
      toast.success("Préférences personnelles enregistrées.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible d'enregistrer vos préférences."));
    } finally {
      setSavingPrefs(false);
    }
  };


  const savePlatform = async () => {
    try {
      await notificationSettings.updatePlatform({ rules: platformRules });
      toast.success("Notifications plateforme enregistrées.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible d'enregistrer la plateforme."));
    }
  };


  const enableMobilePush = async () => {
    setEnablingPush(true);

    try {
      const environment =
        actaPushEnvironment();

      if (
        environment.isIos &&
        !environment.isStandalone
      ) {
        throw new Error(
          "Ajoutez AlmaCRM à l’écran d’accueil, puis ouvrez l’application installée avant d’activer les notifications."
        );
      }

      if (
        !environment.isSecureContext
      ) {
        throw new Error(
          "Une connexion HTTPS sécurisée est nécessaire pour activer les notifications."
        );
      }

      const apiSupported =
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window;

      if (!apiSupported) {
        throw new Error(
          "Les notifications push ne sont pas supportées dans ce navigateur ou ce mode d’ouverture."
        );
      }

      await navigator
        .serviceWorker
        .register(
          "/sw.js",
          {
            scope: "/",
            updateViaCache: "none",
          }
        );

      const registration =
        await navigator
          .serviceWorker
          .ready;

      await registration
        .update()
        .catch(() => {});

      const permission =
        Notification.permission ===
          "granted"
          ? "granted"
          : await Notification
              .requestPermission();

      if (permission !== "granted") {
        throw new Error(
          "L’autorisation des notifications n’a pas été accordée."
        );
      }

      const keyResponse =
        await notificationSettings
          .pushPublicKey();

      const publicKey =
        String(
          keyResponse?.publicKey ||
          ""
        ).trim();

      if (!publicKey) {
        throw new Error(
          "La clé publique du service push est indisponible."
        );
      }

      let subscription =
        await registration
          .pushManager
          .getSubscription();

      if (!subscription) {
        subscription =
          await registration
            .pushManager
            .subscribe({
              userVisibleOnly: true,
              applicationServerKey:
                urlBase64ToUint8Array(
                  publicKey
                ),
            });
      }

      await notificationSettings
        .subscribePush(
          subscription.toJSON()
        );

      const finalStatus =
        await readPushStatus();

      if (!finalStatus.subscribed) {
        throw new Error(
          "L’abonnement a été créé, mais le navigateur ne le retrouve pas."
        );
      }

      toast.success(
        "Push activé sur cet appareil."
      );
    } catch (error) {
      toast.error(
        errorMessage(
          error,
          "Impossible d'activer le push."
        )
      );

      await readPushStatus();
    } finally {
      setEnablingPush(false);
    }
  };


  const testEmail = async () => {
    setTestingEmail(true);
    try {
      await notificationSettings.testEmail();
      toast.success("E-mail de test envoyé.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible d'envoyer l'e-mail de test."));
    } finally {
      setTestingEmail(false);
    }
  };


  const testPush = async () => {
    setTestingPush(true);
    try {
      await notificationSettings.testPush();
      toast.success("Notification push de test envoyée.");
    } catch (error) {
      toast.error(errorMessage(error, "Impossible d'envoyer le push de test."));
    } finally {
      setTestingPush(false);
    }
  };


  const pushStatusLabel =
    actaPushStatusLabel(
      pushStatus
    );

  const pushHelpText =
    actaPushHelpText(
      pushStatus
    );

  const iosInstallHelp =
    pushStatus.isIos &&
    !pushStatus.isStandalone;

  const canStartPushActivation =
    !enablingPush &&
    !pushStatus.subscribed &&
    pushStatus.permission !==
      "denied" &&
    pushStatus.isSecureContext &&
    (
      pushStatus.supported ||
      iosInstallHelp
    );

  const pushActivationLabel =
    enablingPush
      ? "Activation..."
      : iosInstallHelp
        ? "Instructions iPhone"
        : pushStatus.reason ===
            "server_sync_error"
          ? "Réessayer"
          : "Activer sur ce mobile";


  if (loading) {
    return (
      <div className={embedded ? "w-full" : "mx-auto max-w-[1400px] p-5 lg:p-8"}>
        <div className="label-eyebrow lirah-pulse">Chargement</div>
      </div>
    );
  }


  return (
    <div
      className={
        embedded
          ? "w-full space-y-4 pb-28 md:pb-0"
          : "mx-auto max-w-[1400px] space-y-6 p-5 pb-28 md:pb-5 lg:p-8"
      }
      data-testid="notification-settings-page"
    >
      {!embedded && (
        <header className="lirah-card p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
              <BellSimple size={30} weight="duotone" />
            </div>
            <div>
              <div className="label-eyebrow">Paramètres</div>
              <h1 className="mt-1 font-heading text-3xl font-black tracking-tight text-[var(--do-ink)] lg:text-4xl">
                Notifications
              </h1>
              <p className="mt-2 text-sm text-[var(--do-muted)] lg:text-base">
                Choisissez chaque notification reçue par e-mail ou push.
              </p>
            </div>
          </div>
        </header>
      )}


      <section className="lirah-card px-4 py-3 sm:px-5" data-testid="notification-channel-tests">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="min-w-0 flex-[1_1_13rem]">
            <h2 className="font-heading text-sm font-black text-[var(--do-ink)]">
              Tester mes canaux
            </h2>
            <p className="mt-0.5 text-[11px] text-[var(--do-muted)]">
              Vérifiez vos paramètres de réception.
            </p>
            <p className="mt-0.5 text-[10px] leading-4 text-[var(--do-muted)]">
              Par défaut, tout est désactivé. Activez uniquement les notifications que vous souhaitez recevoir.
            </p>
          </div>

          <div className="grid min-w-0 flex-[2_1_35rem] grid-cols-[repeat(auto-fit,minmax(min(12rem,100%),1fr))] items-center gap-2">
            <div className="flex min-w-0 items-center gap-2 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2">
              <EnvelopeSimple size={17} className="shrink-0 text-[var(--do-accent)]" />
              <span className="truncate text-[11px] text-[var(--do-muted)]">
                {user?.email || "Aucune adresse enregistrée"}
              </span>
            </div>
            <button
              type="button"
              onClick={testEmail}
              disabled={testingEmail || !user?.email}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-[var(--do-accent)] px-3 text-[11px] font-bold text-[var(--do-accent)] disabled:opacity-50"
              data-testid="test-email-notification"
            >
              <EnvelopeSimple size={15} />
              {testingEmail ? "Envoi..." : "Tester l’e-mail"}
            </button>

            <div className="flex min-w-0 items-center gap-2 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2">
              <DeviceMobile size={17} className="shrink-0 text-[var(--do-accent)]" />
              <span className="truncate text-[11px] text-[var(--do-muted)]">
                {pushStatusLabel}
              </span>
            </div>
            {!pushStatus.subscribed && (
              <button
                type="button"
                onClick={enableMobilePush}
                disabled={!canStartPushActivation}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-3 text-[11px] font-bold text-[var(--do-ink)] disabled:opacity-50"
                data-testid="enable-mobile-push"
                title={pushHelpText}
              >
                <DeviceMobile size={15} />
                {pushActivationLabel}
              </button>
            )}
            <button
              type="button"
              onClick={testPush}
              disabled={testingPush || !pushStatus.subscribed}
              title={pushStatus.subscribed ? "Envoyer une notification push de test" : pushHelpText}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-[var(--do-accent)] px-3 text-[11px] font-bold text-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="test-push-notification"
            >
              <BellSimple size={15} />
              {testingPush ? "Envoi..." : "Tester le push"}
            </button>
          </div>
        </div>
      </section>


      {!isSaasAdmin && (
        <>
          <section className="lirah-card overflow-hidden" data-testid="notification-accordion">
            <div className="divide-y divide-[var(--do-border)]">
              {groupedEvents.map(({ category, items }) => {
                const isOpen = openCategory === category;
                const categoryLabel = CATEGORY_META[category]?.label || category;

                return (
                  <section
                    key={category}
                    className="notification-settings-group"
                    data-testid={`notification-group-${category}`}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--do-bg-2)] sm:px-5"
                      onClick={() => setOpenCategory(isOpen ? null : category)}
                      aria-expanded={isOpen}
                      aria-controls={`notification-panel-${category}`}
                    >
                      <CaretDown
                        size={16}
                        weight="bold"
                        className={`shrink-0 text-[var(--do-muted)] transition-transform ${isOpen ? "" : "-rotate-90"}`}
                      />

                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-black text-[var(--do-ink)]">
                          {categoryLabel}
                        </h3>
                        <p className="mt-0.5 truncate text-[11px] text-[var(--do-muted)]">
                          {CATEGORY_META[category]?.description}
                        </p>
                      </div>

                      <span className="shrink-0 rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[10px] font-bold text-[var(--do-accent)]">
                        {items.length} réglage{items.length > 1 ? "s" : ""}
                      </span>
                    </button>

                    {isOpen && (
                      <div
                        id={`notification-panel-${category}`}
                        className="border-t border-[var(--do-border)] px-4 pb-4 pt-3 sm:px-5"
                      >
                        <div
                          className="space-y-2 md:hidden"
                          data-testid={`notification-mobile-group-${category}`}
                        >
                          {items.map(({ eventKey, event }) => (
                            <article
                              key={`mobile-${eventKey}`}
                              className="overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                              data-testid={`notification-mobile-event-${eventKey}`}
                            >
                              <div className="px-3 py-3">
                                <div className="text-xs font-black text-[var(--do-ink)]">
                                  {event.label}
                                </div>
                                {event.description && (
                                  <div className="mt-1 text-[10px] leading-4 text-[var(--do-muted)]">
                                    {event.description}
                                  </div>
                                )}
                              </div>

                              <div className="grid grid-cols-2 border-t border-[var(--do-border)]">
                                <div className="flex min-h-11 items-center justify-between gap-2 px-3">
                                  <span className="text-[11px] font-bold text-[var(--do-ink)]">E-mail</span>
                                  <ChannelSwitch
                                    checked={Boolean(prefs.events?.[eventKey]?.email)}
                                    disabled={!roles?.[role]?.[eventKey]?.email}
                                    label={`E-mail — ${event.label}`}
                                    onChange={() => togglePreference(eventKey, "email")}
                                    testId={`toggle-email-${eventKey}`}
                                  />
                                </div>
                                <div className="flex min-h-11 items-center justify-between gap-2 border-l border-[var(--do-border)] px-3">
                                  <span className="text-[11px] font-bold text-[var(--do-ink)]">Push</span>
                                  <ChannelSwitch
                                    checked={Boolean(prefs.events?.[eventKey]?.push)}
                                    disabled={!roles?.[role]?.[eventKey]?.push}
                                    label={`Push — ${event.label}`}
                                    onChange={() => togglePreference(eventKey, "push")}
                                    testId={`toggle-push-${eventKey}`}
                                  />
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>

                        <div className="hidden overflow-x-auto rounded-xl border border-[var(--do-border)] md:block">
                          <table className="w-full min-w-[760px] text-left text-xs">
                            <thead className="bg-[var(--do-bg-solid)] text-[var(--do-muted)]">
                              <tr>
                                <th className="px-4 py-2.5">Type</th>
                                <th className="w-28 px-4 py-2.5 text-center">Dans Acta</th>
                                <th className="w-24 px-4 py-2.5 text-center">E-mail</th>
                                <th className="w-24 px-4 py-2.5 text-center">Push</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map(({ eventKey, event }) => (
                                <tr
                                  key={eventKey}
                                  className="border-t border-[var(--do-border)]"
                                  data-testid={`notification-event-${eventKey}`}
                                >
                                  <td className="px-4 py-2.5">
                                    <div className="font-bold text-[var(--do-ink)]">
                                      {event.label}
                                    </div>
                                    {event.description && (
                                      <div className="mt-0.5 text-[10px] text-[var(--do-muted)]">
                                        {event.description}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2 py-1 text-[10px] font-bold text-[var(--do-accent)]">
                                      <CheckCircle size={13} weight="fill" />
                                      Actif
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <ChannelSwitch
                                      checked={Boolean(prefs.events?.[eventKey]?.email)}
                                      disabled={!roles?.[role]?.[eventKey]?.email}
                                      label={`E-mail — ${event.label}`}
                                      onChange={() => togglePreference(eventKey, "email")}
                                      testId={`toggle-email-${eventKey}`}
                                    />
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <ChannelSwitch
                                      checked={Boolean(prefs.events?.[eventKey]?.push)}
                                      disabled={!roles?.[role]?.[eventKey]?.push}
                                      label={`Push — ${event.label}`}
                                      onChange={() => togglePreference(eventKey, "push")}
                                      testId={`toggle-push-${eventKey}`}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>

            <div
              className="fixed inset-x-0 bottom-0 z-50 flex justify-center border-t border-[var(--do-border)] bg-[var(--do-bg-solid)]/95 px-4 pt-3 shadow-[var(--do-shadow-card)] backdrop-blur md:static md:justify-end md:bg-transparent md:px-5 md:py-3 md:shadow-none md:backdrop-blur-none"
              style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
              data-testid="notification-save-bar"
            >
              <button
                type="button"
                onClick={savePrefs}
                disabled={savingPrefs}
                className="inline-flex h-11 w-full max-w-[680px] items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-5 text-xs font-black text-[var(--do-on-accent)] disabled:opacity-50 md:w-auto"
                data-testid="save-notification-preferences"
              >
                <CheckCircle size={17} weight="fill" />
                {savingPrefs ? "Enregistrement..." : "Enregistrer mes choix"}
              </button>
            </div>
          </section>

          <p
            className="flex items-center gap-2 px-1 text-[11px] leading-4 text-[var(--do-muted)]"
            data-testid="notification-personal-only-info"
          >
            <LockKey size={14} className="shrink-0" />
            Votre périmètre reste protégé : seules vos préférences s’appliquent à vos notifications.
          </p>
        </>
      )}


      {isSaasAdmin && (
        <section className="lirah-card p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <LockKey size={24} className="text-[var(--do-accent)]" />
            <div>
              <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
                Notifications plateforme
              </h2>
              <p className="mt-1 text-xs text-[var(--do-muted)]">
                Sécurité, contact et facturation restent obligatoires.
              </p>
            </div>
          </div>

          <div className="mt-5 hidden md:block overflow-x-auto rounded-2xl border border-[var(--do-border)]">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-[var(--do-bg-solid)] text-[var(--do-muted)]">
                <tr>
                  <th className="px-4 py-3">Événement</th>
                  <th className="px-4 py-3 text-center">E-mail</th>
                  <th className="px-4 py-3 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(platformEvents).map(([eventKey, event]) => (
                  <tr key={eventKey} className="border-t border-[var(--do-border)]">
                    <td className="px-4 py-3">
                      <div className="font-bold text-[var(--do-ink)]">{event.label}</div>
                      <div className="mt-1 text-[10px] text-[var(--do-muted)]">
                        {event.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked
                        disabled
                        readOnly
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2 py-1 text-[10px] font-bold text-[var(--do-accent)]">
                        <LockKey size={13} />
                        Obligatoire
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </section>
      )}
    </div>
  );
}
